// src/lib/portal/PortalContext.tsx
//
// React context for the professional portal (M2-02).
// The signed-in user's customer is resolved SERVER-SIDE through
// /api/portal/me (immutable Keycloak sub → customer_identities →
// customers). No email matching, no first-customer fallback:
// unmapped identities get customer=null and the portal shows its
// "no customer linked" state instead of demo data.

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { z } from "zod";
import { type CustomerAccount } from "@/lib/demoData";
import { useAuth } from "@/components/auth/AuthContext";
import { authHeaders } from "@/lib/api";

export interface PortalContextValue {
  customer: CustomerAccount | null;
  isPortalUser: boolean;
  /** true while /api/portal/me is in flight. */
  loading: boolean;
}

const PortalContext = createContext<PortalContextValue>({
  customer: null,
  isPortalUser: false,
  loading: true,
});

const nullableText = z.string().nullable().default(null);
const customerSchema = z.object({
  customer_id: z.number().int().positive().safe(),
  name: z.string().refine((name) => name.trim().length > 0),
  vat_number: nullableText,
  vat_regime: nullableText,
  country: nullableText,
  street: nullableText,
  street_full: nullableText,
  city: nullableText,
  zip_city: nullableText,
  email: nullableText,
  phone: nullableText,
  status: nullableText,
  created_date: nullableText,
});
type ApiCustomerRow = z.infer<typeof customerSchema>;
const mappingSchema = z.object({ customer: customerSchema });

/** Map the real FileMaker customer row onto the portal's account shape.
 * pricingTier is pinned to "standard": tier discounts are NOT approved
 * until the business pricing policy lands (M2 contract §4). */
export function mapPortalCustomer(r: ApiCustomerRow): CustomerAccount {
  return {
    id: String(r.customer_id),
    companyName: r.name,
    contactName: "",
    email: r.email ?? "",
    country: r.country ?? "",
    pricingTier: "standard",
    monthlyLimitEUR: 0,
    shipTo: [r.street_full, r.zip_city, r.country].filter(
      (x): x is string => Boolean(x),
    ),
    activeSince: r.created_date ?? "",
  };
}

export function PortalProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { sub, issuer } = auth.user ?? {};
  const validPart = (value: unknown): value is string =>
    typeof value === "string" && value.length > 0 && !/[\s\u0000-\u001f\u007f]/.test(value);
  const principal = auth.isReady && auth.isAuthenticated && validPart(sub) && sub.length <= 255 && validPart(issuer)
    ? JSON.stringify([issuer, sub, auth.accountType]) : null;

  // The key replaces mapping state at render time, before a new owner can see
  // the previous account. Mutable profile/group objects are not mapping authority.
  // Any authenticated mapped identity remains eligible under the M2 /me contract;
  // invoice-specific customer-role checks belong to the invoice reader, not here.
  return principal !== null
    ? <PortalMapping key={principal}>{children}</PortalMapping>
    : <PortalContext.Provider value={{ customer: null, isPortalUser: false, loading: !auth.isReady }}>
      {children}
    </PortalContext.Provider>;
}

function PortalMapping({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const request = useRef<AbortController | null>(null);

  // Invalidate at unmount commit, not only at passive effect cleanup. This also
  // blocks deferred fetch/JSON continuations released by another layout effect.
  useLayoutEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    const controller = new AbortController();
    request.current = controller;
    fetch("/api/portal/me", {
      method: "GET", headers: authHeaders(), credentials: "same-origin", cache: "no-store",
      signal: controller.signal,
    })
      .then(async (r) => {
        if (controller.signal.aborted) return;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = mappingSchema.parse(await r.json());
        if (!controller.signal.aborted) setCustomer(mapPortalCustomer(body.customer));
      })
      .catch(() => {
        if (!controller.signal.aborted) setCustomer(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const value = useMemo<PortalContextValue>(
    () => ({ customer, isPortalUser: !!customer, loading }),
    [customer, loading],
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal(): PortalContextValue {
  return useContext(PortalContext);
}
